import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Building2 } from 'lucide-react';
import { EditPropertyData } from '@/schema/properties';
import { PropertyPhotoUpload } from '@/components/properties/create/property-photo-upload';

interface BasicInformationProps {
  form: UseFormReturn<EditPropertyData>;
}

export function BasicInformation({ form }: BasicInformationProps) {
  const {
    control,
    formState: { errors },
  } = form;
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className={cn('flex items-center gap-2')} dir="ltr">
          <Building2 className="w-5 h-5" />
          {t('properties.edit.basicInfo.title')}
        </CardTitle>
        <CardDescription className={isRTL ? 'text-right' : 'text-left'}>
          {t('properties.edit.basicInfo.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name" className={cn(isRTL && 'text-right')}>
              {t('properties.edit.basicInfo.propertyName')} *
            </Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  id="name"
                  {...field}
                  className={cn(
                    errors.name ? 'border-destructive' : '',
                    isRTL ? 'text-right' : 'text-left'
                  )}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              )}
            />
            {errors.name && (
              <p
                className={cn(
                  'text-sm text-destructive',
                  isRTL && 'text-right'
                )}
              >
                {errors.name.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="location" className={cn(isRTL && 'text-right')}>
              {t('properties.edit.basicInfo.location')} *
            </Label>
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <Input
                  id="location"
                  {...field}
                  className={cn(
                    errors.location ? 'border-destructive' : '',
                    isRTL ? 'text-right' : 'text-left'
                  )}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              )}
            />
            {errors.location && (
              <p
                className={cn(
                  'text-sm text-destructive',
                  isRTL && 'text-right'
                )}
              >
                {errors.location.message}
              </p>
            )}
          </div>
        </div>

        <PropertyPhotoUpload />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="stars" className={cn(isRTL && 'text-right')}>
              {t('properties.edit.basicInfo.starRating')}
            </Label>
            <Controller
              name="stars"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value?.toString()}
                  onValueChange={value => field.onChange(parseInt(value))}
                >
                  <SelectTrigger
                    id="stars"
                    className={cn(
                      errors.stars ? 'border-destructive' : '',
                      isRTL ? 'flex-row-reverse' : 'flex-row'
                    )}
                  >
                    <SelectValue
                      placeholder={t(
                        'properties.create.basicInfo.fields.starsPlaceholder'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">
                      {t('properties.create.basicInfo.starOptions.1')}
                    </SelectItem>
                    <SelectItem value="2">
                      {t('properties.create.basicInfo.starOptions.2')}
                    </SelectItem>
                    <SelectItem value="3">
                      {t('properties.create.basicInfo.starOptions.3')}
                    </SelectItem>
                    <SelectItem value="4">
                      {t('properties.create.basicInfo.starOptions.4')}
                    </SelectItem>
                    <SelectItem value="5">
                      {t('properties.create.basicInfo.starOptions.5')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.stars && (
              <p
                className={cn(
                  'text-sm text-destructive',
                  isRTL && 'text-right'
                )}
              >
                {errors.stars.message}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
